import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import AuthenticatedImage from "../../shared/AuthenticatedImage.tsx";
import { getStoreCategories } from "../storefrontService.ts";
import ShopBreadcrumb from "../ShopBreadcrumb.tsx";
import { findCategoryPath, type StoreCategoryNode } from "./categoryTree.ts";
import { buildCategoryPath } from "../categoryUrl.ts";
import "./CategoryNavigation.css";

type CategoryNavigationProps = {
  slug?: string;
  title: string;
  actions?: ReactNode;
};

export default function CategoryNavigation({ slug, title, actions }: CategoryNavigationProps) {
  const storeLanguage = useStoreLanguage();
  const { pathname, search } = useLocation();
  const [categories, setCategories] = useState<StoreCategoryNode[]>([]);
  useEffect(function () {
    let active = true;
    if (slug) {
      getStoreCategories().then(function (result) {
        if (active) setCategories(result);
      }).catch(function () {
        if (active) setCategories([]);
      });
    }
    return function () { active = false; };
  }, [storeLanguage, slug]);
  const path = slug ? findCategoryPath(categories, slug) : [];
  const selected = path[path.length - 1];
  const hasSearchQuery = Boolean(new URLSearchParams(search).get("q"));
  const pageTitle = selected && !hasSearchQuery ? selected.name : title;
  const hierarchy = selected
    ? (hasSearchQuery ? path : path.slice(0, -1)).slice().reverse()
    : [];
  const breadcrumbItems = [
    { title: pageTitle },
    ...hierarchy.map(function (category) {
      return {
        title: category.name,
        href: buildCategoryPath(category.slug),
      };
    }),
    ...(selected ? [{ title: t("همهٔ دسته‌بندی‌ها"), href: "/categories" }] : []),
    ...(pathname !== "/" ? [{ title: t("خانه"), href: "/" }] : []),
  ];
  const hasCategoryContext = Boolean(
    selected && (selected.description || selected.childCategories.length),
  );

  return (
    <ShopBreadcrumb
      className={`shop-breadcrumb--products${!slug && !hasSearchQuery ? " shop-breadcrumb--all-products" : ""}`}
      hierarchy
      items={breadcrumbItems}
      actions={actions}
      navigation={hasCategoryContext ? (
        <div className="shop-category-navigation">
          {selected?.description ? (
            <p className="shop-category-navigation__description">
              {selected.description}
            </p>
          ) : null}
          {selected?.childCategories.length ? (
            <nav
              aria-label={t("زیر‌دسته‌های {{value0}}", { value0: selected.name })}
              className="shop-category-navigation__children"
            >
              {selected.childCategories.map(function (child) {
                return (
                  <Link
                    key={child.id}
                    to={buildCategoryPath(child.slug)}
                    className="shop-category-navigation__tile"
                  >
                    <span className="shop-category-navigation__artwork" aria-hidden="true">
                      <AuthenticatedImage
                        fileId={child.fileId}
                        alt=""
                        thumbnailHeight={240}
                        disablePreview={true}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </span>
                    <span className="shop-category-navigation__label">{child.name}</span>
                  </Link>
                );
              })}
            </nav>
          ) : null}
        </div>
      ) : null}
    />
  );
}
